import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader, StatCard } from "@/components/PageHelpers";
import { TourLauncherButton } from "@/contexts/TourContext";
import { Card } from "@/components/ui/card";
import { ActivityTicker } from "@/components/ActivityTicker";
import { OccupancyMapCard } from "@/components/OccupancyMapCard";
import { Users, Home, Wrench, AlertTriangle, ClipboardList, UserCheck, DollarSign, TrendingDown, FileDown, FileSignature, Sparkles, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { SendViaDocuSignDialog } from "@/components/DocuSignEnvelope";
import { HealthzStatusCard } from "@/components/HealthzStatusCard";
import { AdminVehicleApprovalsCard } from "@/components/AdminVehicleApprovalsCard";
import DeliveryInsightsCard from "@/components/DeliveryInsightsCard";
import OnboardingWatchtowerCard from "@/components/OnboardingWatchtowerCard";
import ImpersonationHistoryCard from "@/components/ImpersonationHistoryCard";
import AdminElectionTurnoutCard from "@/components/AdminElectionTurnoutCard";

const AGING_LABEL = { current: "Current", "30_60": "30–60 days", "60_90": "60–90 days", "90_plus": "90+ days" };

export default function AdminDashboard() {
  const { propertyId, currentProperty } = useProperty();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signers, setSigners] = useState([
    { name: "", role: "Board Chair", signature_dataurl: null },
    { name: "", role: "Treasurer", signature_dataurl: null },
  ]);
  const [signing, setSigning] = useState(false);
  const [useAiNarrative, setUseAiNarrative] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      const { data } = await api.get(`/dashboard/admin?property_id=${propertyId}`);
      setData(data);
    })();
  }, [propertyId]);

  const downloadPacket = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/board-packet.pdf?property_id=${propertyId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-packet-${propertyId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Draft packet downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
    setDownloading(false);
  };

  const updateSigner = (idx, key, value) => {
    setSigners((prev) => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  };
  const addSigner = () => {
    if (signers.length >= 5) return;
    setSigners((prev) => [...prev, { name: "", role: "Board Member", signature_dataurl: null }]);
  };
  const removeSigner = (idx) => {
    setSigners((prev) => prev.filter((_, i) => i !== idx));
  };

  const generateNarrative = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/board-report", { property_id: propertyId });
      setNarrative(data.narrative || "");
      toast.success("AI narrative generated");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setGenerating(false);
  };

  const signAndDownload = async () => {
    const cleaned = signers.filter((s) => s.name.trim());
    if (cleaned.length === 0) return toast.error("Add at least one signer");
    setSigning(true);
    try {
      const res = await api.post(`/reports/board-packet/sign-multi`, {
        property_id: propertyId,
        signers: cleaned.map((s) => ({
          name: s.name.trim(),
          role: (s.role || "Board Member").trim(),
          signature_dataurl: s.signature_dataurl || null,
        })),
        ai_narrative: useAiNarrative && narrative ? narrative : null,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-packet-signed-${propertyId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Ratified with ${cleaned.length} signature${cleaned.length === 1 ? "" : "s"}`);
      setSignOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSigning(false);
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const k = data.kpis;

  return (
    <div>
      <PageHeader
        eyebrow="Command Center"
        title={currentProperty?.name || "Dashboard"}
        subtitle={currentProperty?.address}
        testid="admin-dashboard-header"
        actions={
          <div className="flex items-center gap-2">
            <TourLauncherButton name="dashboard_tour" />
            <Button
              data-testid="download-board-packet"
              data-tour="dashboard-packet"
              onClick={downloadPacket}
              disabled={downloading}
              variant="outline"
              className="rounded-full bg-white/[0.04] hover:bg-white/[0.1] border border-white/15 text-white"
            >
              <FileDown className="h-4 w-4 mr-1.5" />
              {downloading ? "Preparing…" : "Draft PDF"}
            </Button>
            <Button
              data-testid="sign-board-packet"
              onClick={() => setSignOpen(true)}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <FileSignature className="h-4 w-4 mr-1.5" /> Sign & Ratify
            </Button>
            <SendViaDocuSignDialog
              propertyId={propertyId}
              defaultSigners={[
                { name: "", email: "", role: "Board Chair" },
                { name: "", email: "", role: "Treasurer" },
              ]}
              trigger={
                <Button
                  data-testid="send-via-docusign"
                  variant="outline"
                  className="rounded-full border-emerald-500/30 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20"
                >
                  <FileSignature className="h-4 w-4 mr-1.5" /> DocuSign
                </Button>
              }
            />
          </div>
        }
      />

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl">
          <DialogHeader><DialogTitle>Ratify Board Packet</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-400 mb-3">
            Draw your signature (or type-only if you prefer). Up to 5 signers · each embedded with
            a unique attest ID, cryptographic hash, timestamp, and audit log entry.
          </div>

          <Card className="border-indigo-500/30 bg-indigo-500/[0.05] p-3 mb-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-300" />
                  <div className="text-sm font-medium">AI executive summary</div>
                </div>
                <div className="text-xs text-zinc-400">Insert an AI-generated cover narrative for this month&apos;s packet.</div>
              </div>
              <Switch checked={useAiNarrative} onCheckedChange={setUseAiNarrative} data-testid="use-ai-narrative" />
            </div>
            {useAiNarrative && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-indigo-500/30 text-indigo-200 hover:bg-indigo-500/10"
                    onClick={generateNarrative}
                    disabled={generating}
                    data-testid="generate-ai-narrative"
                  >
                    <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (generating ? "animate-spin" : "")} />
                    {generating ? "Generating…" : narrative ? "Regenerate" : "Generate"}
                  </Button>
                </div>
                <Textarea
                  data-testid="ai-narrative-body"
                  rows={6}
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  placeholder="Click Generate to draft an executive summary via Claude Sonnet 4.5."
                  className="text-xs"
                />
              </div>
            )}
          </Card>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto scroll-slim pr-1">
            {signers.map((s, idx) => (
              <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Signature {idx + 1}</div>
                  {signers.length > 1 && (
                    <button
                      className="text-xs text-rose-300 hover:text-rose-200"
                      onClick={() => removeSigner(idx)}
                      data-testid={`remove-signer-${idx}`}
                    >Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <div>
                    <Label className="text-xs">Signer name</Label>
                    <Input
                      data-testid={`signer-name-${idx}`}
                      value={s.name}
                      onChange={(e) => updateSigner(idx, "name", e.target.value)}
                      placeholder="e.g. Chris Vela"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Input
                      data-testid={`signer-role-${idx}`}
                      value={s.role}
                      onChange={(e) => updateSigner(idx, "role", e.target.value)}
                    />
                  </div>
                </div>
                <Label className="text-xs">Draw signature (leave blank for typed)</Label>
                <SignaturePad
                  testid={`signer-pad-${idx}`}
                  onChange={(dataUrl) => updateSigner(idx, "signature_dataurl", dataUrl)}
                />
              </div>
            ))}
          </div>
          {signers.length < 5 && (
            <Button
              variant="outline"
              onClick={addSigner}
              data-testid="add-signer"
              className="w-full border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.05] text-zinc-300"
            >
              + Add another signer
            </Button>
          )}
          <Button
            data-testid="sign-submit"
            onClick={signAndDownload}
            disabled={signing}
            className="w-full bg-indigo-600 hover:bg-indigo-700 mt-2"
          >
            {signing ? "Signing…" : <><FileSignature className="h-4 w-4 mr-1.5" />Sign & download</>}
          </Button>
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-tour="dashboard-kpis">
        <StatCard label="Residents" value={k.residents} hint={`${k.occupancy_pct}% occupancy`} icon={Users} testid="kpi-residents" />
        <StatCard label="Revenue MTD" value={"$" + Number(k.revenue_mtd).toLocaleString()} icon={DollarSign} accent="green" testid="kpi-revenue" />
        <StatCard label="Total Delinquent" value={"$" + Number(k.total_delinquent).toLocaleString()} icon={TrendingDown} accent="red" testid="kpi-delinquent" />
        <StatCard label="Pending Approvals" value={k.pending_approvals} icon={UserCheck} accent="indigo" testid="kpi-approvals" />
      </div>

      <div className="mt-4" data-testid="healthz-slot">
        <HealthzStatusCard />
      </div>

      <AdminVehicleApprovalsCard propertyId={propertyId} />

      <div className="mt-4">
        <AdminElectionTurnoutCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
        <Card className="lg:col-span-8 p-5 bg-card border-white/10" data-tour="dashboard-collections">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Collections Trend</div>
              <div className="text-lg font-semibold font-display">6-month collected vs delinquent</div>
            </div>
            <Badge className="bg-orange-500/10 text-orange-300 border-orange-500/30" variant="outline">USD</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.collection_trend}>
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "white" }} />
                <Legend />
                <Bar dataKey="collected" fill="#F97316" radius={[6,6,0,0]} />
                <Bar dataKey="delinquent" fill="#3f3f46" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-4 p-5 bg-card border-white/10">
          <div className="overline mb-3">Recent Activity</div>
          <div className="space-y-3">
            {(data.recent_activity || []).map((e, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                className="flex items-start gap-3 pb-3 border-b border-white/5 last:border-none last:pb-0"
                data-testid={`activity-${i}`}
              >
                <div className="mt-1 h-2 w-2 rounded-full bg-orange-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.title}</div>
                  <div className="text-xs text-zinc-500">{e.kind} · {e.meta}</div>
                </div>
              </motion.div>
            ))}
            {(data.recent_activity || []).length === 0 && (
              <div className="text-sm text-zinc-500">No recent activity.</div>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <OccupancyMapCard />
      </div>

      <div className="mt-4">
        <ActivityTicker limit={20} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-5 bg-card border-white/10" data-tour="dashboard-aging">
          <div className="overline mb-3">Aging Buckets</div>
          <div className="space-y-3">
            {Object.entries(data.aging).map(([b, v]) => (
              <div key={b} className="flex items-center justify-between" data-testid={`aging-${b}`}>
                <div className="text-sm">{AGING_LABEL[b] || b}</div>
                <div className={"tabular-nums font-medium " + (b === "current" ? "text-emerald-400" : "text-rose-400")}>
                  ${Number(v).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 bg-card border-white/10">
          <div className="overline mb-3">Open Work</div>
          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="Maintenance" value={k.open_maintenance} icon={Wrench} />
            <MiniStat label="Violations" value={k.open_violations} icon={AlertTriangle} />
            <MiniStat label="ARC" value={k.open_arc} icon={ClipboardList} />
          </div>
        </Card>

        <DeliveryInsightsCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <OnboardingWatchtowerCard />
        <ImpersonationHistoryCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-5 bg-card border-white/10">
          <div className="overline mb-3">Property Portfolio</div>
          <div className="flex items-center gap-4">
            <img
              src={currentProperty?.hero_image}
              alt={currentProperty?.name}
              className="h-20 w-24 rounded-md object-cover border border-white/10"
            />
            <div className="min-w-0">
              <div className="font-medium truncate">{currentProperty?.name}</div>
              <div className="text-xs text-zinc-500">{currentProperty?.units} units · {currentProperty?.type}</div>
              <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                <Home className="h-3 w-3" /> {currentProperty?.address}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-2xl font-semibold font-display tabular-nums mt-1">{value}</div>
    </div>
  );
}
